import { toast } from 'sonner';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  PURCHASE_EMAIL_REQUIRED_MESSAGE,
  signupBasicDetailsWithEmailSchema,
  signupBasicDetailsWithGoogleSchema,
  signupOtherDetailsSchema,
} from '@/redux/auth/auth.schema';
import BasicDetails from './Carousel/BasicDetails';
import { AuthProvider } from '@/redux/auth/auth.d';
import OtherDetails from './Carousel/OtherDetails';
import { selectCurrentUser } from '@/redux/auth/auth.slice';
import {
  useCompleteUserSignupMutation,
  useGetUserQuery,
  useGetSignupSessionQuery,
  useLazyGetGoogleSignInLinkQuery,
  useLogoutMutation,
  useSignupMutation,
} from '@/redux/auth/auth.api.slice';
import { setShowLoginDialog } from '@/redux/global/global.slice';
import { LastUsedBadge } from '@/redux/auth/lastUsedProvider';
import { IUserSignupDataWithConfirmPassword } from './signup.types';
import Google from '@/components/Icons/Google';
import Divider from '@/components/ui/divider';
import signupPoster from '@/assets/signupPoster.png';
import filefinityLogo from '@/assets/filefinity_navbar_logo.png';
import onefinityLogo from '@/assets/onefinity_logo.png';

function apiErrorMessage(error: unknown): string | null {
  const message = (error as { data?: { errorMessage?: unknown } } | null)?.data
    ?.errorMessage;
  return typeof message === 'string' ? message : null;
}

function Signup() {
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const formPaneRef = useRef<HTMLDivElement>(null);
  const { user, isAuthenticated } = useSelector(selectCurrentUser);
  const { data: sessionUser } = useGetSignupSessionQuery();
  const effectiveUser = user ?? sessionUser ?? null;
  const [signup, { isLoading: isSignupLoading }] = useSignupMutation();
  const { refetch: refetchUser } = useGetUserQuery();
  const dispatch = useDispatch();

  const [logout] = useLogoutMutation();
  const [getGoogleSignInLink] = useLazyGetGoogleSignInLinkQuery();
  const [completeSignup, { isLoading: isCompleteSignupLoading }] =
    useCompleteUserSignupMutation();
  const navigate = useNavigate();

  const IS_USER_SINGUP_COMPLETE: boolean = Boolean(
    effectiveUser?.isUserSignupComplete
  );
  const IS_SOCIAL_LOGIN: boolean =
    effectiveUser?.authProvider !== AuthProvider.Email;

  const [formData, setFormData] = useState<IUserSignupDataWithConfirmPassword>({
    profilePicture: undefined,
    username: '',
    firstname: '',
    lastname: '',
    email: '',
    cncBrand: [],
    cncModel: [],
    cncVariant: [],
    password: '',
    confirmPassword: '',
    locatedAt: 'Yet to be collected',
  });

  useEffect(() => {
    if (effectiveUser) {
      setFormData((prev) => ({
        ...prev,
        firstname: effectiveUser.firstname,
        lastname: effectiveUser.lastname,
        email: effectiveUser.email,
        cncBrand: effectiveUser.cncBrand ?? [],
        cncModel: effectiveUser.cncModel ?? [],
        cncVariant: [],
        profilePicture: effectiveUser.profilePicture,
      }));
    }
  }, [effectiveUser, isAuthenticated, sessionUser]);

  // The form scrolls in its own pane, so an error rendered above the submit
  // button would otherwise stay off screen. Toast-only failures set no errors.
  useEffect(() => {
    if (!Object.keys(errors).length) {
      return;
    }
    const firstError = formPaneRef.current?.querySelector('[role="alert"]');
    if (!firstError) {
      return;
    }
    firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    firstError.parentElement
      ?.querySelector<HTMLElement>('input, textarea, select')
      ?.focus({ preventScroll: true });
  }, [errors]);

  const handleSignupSubmit = async () => {
    try {
      if (
        !(Boolean(effectiveUser) && IS_SOCIAL_LOGIN && !IS_USER_SINGUP_COMPLETE)
      ) {
        await signup({
          email: formData.email,
          password: formData.password,
          username: formData.username,
          firstname: formData.firstname,
          lastname: formData.lastname,
          cncBrand: formData.cncBrand,
          cncModel: formData.cncModel,
          cncVariant: formData.cncVariant,
          locatedAt: 'Yet to be collected',
          ...(formData.profilePicture
            ? { profilePicture: formData.profilePicture }
            : {}),
        }).unwrap();
        await refetchUser();
      } else {
        await completeSignup({
          username: formData.username,
          firstname: formData.firstname,
          lastname: formData.lastname,
          cncBrand: formData.cncBrand,
          cncModel: formData.cncModel,
          cncVariant: formData.cncVariant,
          locatedAt: 'Yet to be collected',
          password: formData.password,
          ...(formData.profilePicture
            ? { profilePicture: formData.profilePicture }
            : {}),
        }).unwrap();
        await refetchUser();
      }

      navigate('/');
    } catch (error: unknown) {
      const errorMessage = apiErrorMessage(error);

      if (errorMessage === PURCHASE_EMAIL_REQUIRED_MESSAGE) {
        setErrors((current) => ({
          ...current,
          email: PURCHASE_EMAIL_REQUIRED_MESSAGE,
        }));
        return;
      }

      toast.error('Signup Failed', {
        description: errorMessage ?? 'Unable to create your account.',
      });
    }
  };

  // fallow-ignore-next-line complexity
  const handleSubmitAll = async () => {
    const isGoogleCompletion =
      Boolean(effectiveUser) && IS_SOCIAL_LOGIN && !IS_USER_SINGUP_COMPLETE;
    const basicSchema = isGoogleCompletion
      ? signupBasicDetailsWithGoogleSchema
      : signupBasicDetailsWithEmailSchema;

    const [basicResult, otherResult] = await Promise.all([
      basicSchema.safeParseAsync(formData),
      signupOtherDetailsSchema.safeParseAsync(formData),
    ]);

    if (!basicResult.success || !otherResult.success) {
      const merged: { [key: string]: string } = {};
      const collectErrors = (
        issues: { path: (string | number)[]; message: string }[]
      ) => {
        issues.forEach((issue) => {
          const key = String(issue.path[0]);
          if (key && !merged[key]) merged[key] = issue.message;
        });
      };
      if (!basicResult.success) collectErrors(basicResult.error.errors);
      if (!otherResult.success) collectErrors(otherResult.error.errors);
      setErrors(merged);
      toast.error('Please fix the errors before continuing.');
      return;
    }

    setErrors({});
    handleSignupSubmit();
  };

  if (Boolean(effectiveUser) && IS_USER_SINGUP_COMPLETE) {
    navigate('/');
  }

  const handleLogin = async () => {
    await logout();
    await refetchUser();
    navigate('/');
    dispatch(setShowLoginDialog(true));
  };

  const [isGoogleRedirecting, setIsGoogleRedirecting] = useState(false);

  const handleGoogleSignup = async () => {
    setIsGoogleRedirecting(true);
    try {
      const data = await getGoogleSignInLink().unwrap();
      window.location.href = data.url;
      // Keep the overlay up while the browser navigates away.
    } catch (error: any) {
      setIsGoogleRedirecting(false);
      toast.error('Could not start Google sign-up', {
        description: error?.data?.errorMessage || 'Please try again.',
      });
    }
  };

  const isSubmitting = isCompleteSignupLoading || isSignupLoading;
  const isGoogleCompletion =
    Boolean(effectiveUser) && IS_SOCIAL_LOGIN && !IS_USER_SINGUP_COMPLETE;

  return (
    <div className="relative flex h-screen overflow-hidden bg-background font-secondary">
      {/* Close */}
      <button
        type="button"
        onClick={() => navigate('/')}
        aria-label="Close"
        className="absolute right-4 top-4 z-20 flex h-9 w-9 items-center justify-center rounded-full text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="flex flex-1 overflow-hidden">
        {/* Left pane — brand / value proposition */}
        <aside className="relative hidden w-[50%] shrink-0 overflow-hidden lg:block">
          {/* Light blue-gray gradient, lightest through the middle so the
              poster's dark artwork/text reads naturally; top & bottom stay a
              touch deeper so the white logos and stats keep contrast (further
              helped by drop/text shadows). */}
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'radial-gradient(120% 100% at 16% 12%, rgba(255,255,255,0.10), transparent 55%), linear-gradient(160deg, #74859b 0%, #aab8c9 56%, #7e90a6 100%)',
            }}
          />
          <div className="relative z-10 flex h-full flex-col justify-between p-10 text-white xl:p-12">
            <div className="flex items-center gap-3 self-start">
              <img
                src={filefinityLogo}
                alt="Filefinity"
                className="h-12 w-auto object-contain drop-shadow-lg"
              />
              <div className="h-9 w-px bg-white/30" />
              <img
                src={onefinityLogo}
                alt="Onefinity"
                className="h-12 w-auto object-contain drop-shadow-lg"
              />
            </div>

            <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-5 py-4">
              <div className="w-full max-w-md rounded-2xl border border-white/20 bg-slate-900/40 p-6 text-center shadow-xl backdrop-blur-md">
                <h2 className="font-secondary text-3xl font-bold leading-tight">
                  Where Onefinity makers share their work.
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-white/85">
                  Post your builds, download ready-to-cut files, and earn
                  rewards — together with the community.
                </p>
              </div>
              <img
                src={signupPoster}
                alt="Filefinity maker community"
                className="max-h-[42vh] w-auto max-w-sm object-contain drop-shadow-2xl xl:max-w-md"
              />
            </div>

            <div className="flex gap-8 [text-shadow:0_1px_3px_rgba(15,23,42,0.55)]">
              {[
                { n: '53+', l: 'Makers' },
                { n: '76', l: 'Projects' },
                { n: '525', l: 'Downloads' },
              ].map(({ n, l }) => (
                <div key={l}>
                  <div className="font-secondary text-2xl font-bold">{n}</div>
                  <div className="text-xs uppercase tracking-wider text-white/85">
                    {l}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* Right pane — form */}
        <div ref={formPaneRef} className="flex-1 overflow-y-auto bg-white">
          <div className="ff-fade-up mx-auto w-full max-w-2xl px-4 py-6 sm:px-6 sm:py-8">
            {/* Intro */}
            <div className="mb-8">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-green-700">
                Join the community
              </p>
              <h1 className="font-secondary mt-1.5 text-3xl font-bold text-foreground">
                Set up your account
              </h1>
              <p className="mt-1.5 text-sm text-muted-foreground">
                A couple of minutes and you&apos;re in.
              </p>
            </div>

            {/* Google signup (email path only) */}
            {!isGoogleCompletion && (
              <div className="mb-8 space-y-5">
                <div className="relative">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleGoogleSignup}
                    disabled={isGoogleRedirecting}
                    className="h-12 w-full gap-2.5 text-sm font-medium"
                  >
                    {isGoogleRedirecting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Redirecting to Google…
                      </>
                    ) : (
                      <>
                        <Google />
                        Sign up with Google
                      </>
                    )}
                  </Button>
                  <LastUsedBadge provider={AuthProvider.Google} />
                </div>
                <Divider text="or sign up with email" />
              </div>
            )}

            {/* Account details */}
            <BasicDetails
              formData={formData}
              errors={errors}
              setFormData={setFormData}
              user={effectiveUser}
            />

            {/* Machine section */}
            <p className="mb-5 mt-12 text-[11px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
              Your machine
            </p>

            <OtherDetails
              formData={formData}
              errors={errors}
              setFormData={setFormData}
            />

            {/* Submit */}
            <Button
              type="button"
              onClick={handleSubmitAll}
              disabled={isSubmitting}
              className="mt-10 h-12 w-full border-0 bg-[linear-gradient(135deg,#1f8a61_0%,#16704e_55%,#0f4d37_100%)] text-base font-semibold tracking-wide text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.25)] transition-[filter] hover:brightness-110"
            >
              {isSubmitting ? 'Creating account…' : 'Create account'}
            </Button>

            {/* Footer */}
            <div className="mt-6 text-center">
              <p className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <Button
                  variant="link"
                  className="h-auto cursor-pointer border-none bg-transparent p-0 font-semibold text-green-700 underline-offset-2 hover:text-green-600"
                  onClick={handleLogin}
                >
                  Log in
                </Button>
              </p>
            </div>
          </div>
        </div>
      </div>

      {isGoogleRedirecting && (
        <div
          role="status"
          aria-live="polite"
          className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-white/85 backdrop-blur-sm"
        >
          <Loader2 className="h-10 w-10 animate-spin text-green-700" />
          <p className="text-sm font-medium text-foreground">
            Redirecting to Google…
          </p>
          <p className="text-xs text-muted-foreground">
            Hang tight, this takes a moment.
          </p>
        </div>
      )}

      <style>
        {`@keyframes ffFadeUp{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}.ff-fade-up{animation:ffFadeUp .55s cubic-bezier(.16,1,.3,1) both}`}
      </style>
    </div>
  );
}

export default Signup;
